// Angular modules
import { ChangeDetectorRef, Component, OnDestroy, TemplateRef, inject } from '@angular/core';
import { OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

// Internal modules
import { environment } from '@env/environment';
import language from "@core/language-list.json";
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthStorageService } from '@services/auth-storage.service';
import { CommonService } from '@services/common.service';
import { HttpService } from '@services/http.service';
import { HttpParams } from '@angular/common/http';

interface Country_Currency {
  name: string,
  currencies: string[],
  language: string,
  isoCode: string,
  flag: string
}

@Component({
  selector: 'app-layout-header',
  standalone: false,
  templateUrl: './layout-header.component.html',
  styleUrls: ['./layout-header.component.scss']
})
export class LayoutHeaderComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  public appName: string = environment.appName;
  public isMenuCollapsed: boolean = true;
  public propertyUrlSegment!: string;
  public loggedInUser: any;

  languages: any[] = language;
  websiteLanguage: any;
  websiteCountry: any;
  countryCurrencies: Country_Currency[];
  public selectedHotelId: string | null = null;
  public legalNameOfProperty: string | null = null;

  private modalService = inject(NgbModal);
  public createdProperty: any;
  public email_address: string;
  private isAddingNewProperty: boolean;
  public hotelDetails: any;
  private hasSetLegalName: boolean = false;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private commonService: CommonService,
    private httpService: HttpService,
    private authStorage: AuthStorageService
  ) {

  }

  public ngOnInit(): void {
    this.loggedInUser = this.authStorage.getUser();
    // Default the corner identity to the account email so it still shows for
    // accounts with no property. When a property exists the hotel logic below
    // overrides this with the property's contact email.
    this.email_address = this.loggedInUser?.user_data?.email ?? '';
    this.isAddingNewProperty = localStorage.getItem("isAddingNewProperty") === "true";
    this.websiteCountry = this.languages[0];

    if (!this.isAddingNewProperty) {
      // Retrieve the initial selected hotel ID from localStorage
      this.selectedHotelId = localStorage.getItem('selectedHotelId');

      // Set the legal name of the property if the selectedHotelId is available
      if (this.selectedHotelId) {
        this.setLegalNameOfProperty(this.selectedHotelId);
      }
    }

    this.commonService.selectedHotelId$.pipe(takeUntil(this.destroy$)).subscribe(id => {
      this.selectedHotelId = id;
      if (this.selectedHotelId) {
        this.setLegalNameOfProperty(this.selectedHotelId);
      }
    });

    const currentUrl = this.router.url;
    const accountIndex = currentUrl.indexOf('/account');
    const propertyIndex = currentUrl.indexOf('/property');
    const managementIndex = currentUrl.indexOf('/management');
    const walletIndex = currentUrl.indexOf('/wallet');

    if (accountIndex !== -1) {
      this.propertyUrlSegment = currentUrl.substring(0, accountIndex + 8); // Add 8 to include "/account"
    }

    else if (propertyIndex !== -1) {
      this.propertyUrlSegment = currentUrl.substring(0, propertyIndex + 9); // Add 11 to include "/property"
    }

    else if (managementIndex !== -1) {
      this.propertyUrlSegment = currentUrl.substring(0, managementIndex + 11); // Add 11 to include "/management"
    }

    else if (walletIndex !== -1) {
      this.propertyUrlSegment = currentUrl.substring(0, walletIndex + 7); // Add 11 to include "/management"
     this.getHotelsbyUserId();
    }

    else {
      this.propertyUrlSegment = ''; // or handle error
    }

    if (!this.hasSetLegalName) {
      this.getHotelsbyUserId();
    }

  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  getHotelsbyUserId() {
    // No authenticated user (e.g. the public /home page) — nothing to fetch.
    if (!this.loggedInUser?.user_data?.id) {
      return;
    }

     let body = new HttpParams()
          .set('pageNumber', '1')
          .set('per_page', '100')

    this.httpService.getData(`hotel/${this.loggedInUser.user_data.id}`,body).subscribe({
      next: res => {
        if (res.status === 'success') {
          this.hotelDetails = res.data;
          this.loggedInUser.hotel = this.hotelDetails.data;
          this.commonService.setAccountHotels(this.loggedInUser.hotel)
          this.authStorage.setUser(this.loggedInUser);
          if (this.isAddingNewProperty) {
            for (let hotel of this.hotelDetails.data) {
              if (String(hotel.id) === String(this.selectedHotelId)) {
                this.email_address = hotel.email_address;
                this.legalNameOfProperty = hotel.legal_name_of_property;
                break; // Exits the loop once a match is found
              }
              else {
                this.email_address = hotel.email_address;
                this.legalNameOfProperty = hotel.legal_name_of_property;
              }
            }
          } else {
            this.hotelDetails.data.forEach((hotel: { id: string, email_address: string, legal_name_of_property: string }) => {
              this.email_address = hotel.email_address;
              this.legalNameOfProperty = hotel.legal_name_of_property;
            });
          }
        }
      },
      error: err => {
        console.error(err);
      }
    });
  }


  private setLegalNameOfProperty(hotelId: string): void {
    this.hasSetLegalName = true; // Set the flag to true
    if (this.isAddingNewProperty) {
      if (this.hotelDetails && this.hotelDetails.data) {

        // Find the hotel with the matching ID
        const matchedHotel = this.hotelDetails.data.find((hotel: { id: string, legal_name_of_property: string }) =>
          String(hotel.id) === String(hotelId)
        );
        if (matchedHotel) {
          this.legalNameOfProperty = matchedHotel.legal_name_of_property;
        } else {
          // If hotel not found, fetch hotels
          this.getHotelsbyUserId();
        }
      } else {
        // If hotelDetails is undefined or has no data, fetch hotels
        this.getHotelsbyUserId();
      }

    }
    else {
      for (let hotel of this.loggedInUser?.hotel) {
        if (String(hotel.id) === String(hotelId)) {
          this.legalNameOfProperty = hotel.legal_name_of_property;
          this.email_address = hotel.email_address;
          return;
        }
      }
    }
  }

  selectLanguage(language) {
    this.websiteCountry = language
    localStorage.setItem("language", JSON.stringify(language))
    this.modalService.dismissAll()
    const selectElement = document.querySelector('#google_translate_element select');
    selectElement['value'] = language["ISO-code"];
    selectElement.dispatchEvent(new Event('change'));
  }

  // selectCountry(country){
  //   this.websiteCountry=  this.countryCurrencies.find(e => e.isoCode === country);
  //   let currentCurrency = 'NGN';
  //   localStorage.setItem("country", this.websiteCountry.isoCode.toUpperCase())
  //   localStorage.setItem("currency",this.websiteCountry.currencies.includes(currentCurrency) ? currentCurrency : this.websiteCountry.currencies[0])
  //   this.modalService.dismissAll()
  //   window.location.reload();
  // }

  openModalXl(content: TemplateRef<any>) {
    this.modalService.open(content, { centered: true, modalDialogClass: 'custom-modal xl padding-sm' },);
  }


  // -------------------------------------------------------------------------------
  // NOTE Init ---------------------------------------------------------------------
  // -------------------------------------------------------------------------------

  // -------------------------------------------------------------------------------
  // NOTE Actions ------------------------------------------------------------------
  // -------------------------------------------------------------------------------

  // public async onClickLogout(): Promise<void> {
  //   // NOTE Redirect to login
  //   localStorage.clear();
  //   this.router.navigate(['/']);
  //   location.reload();
  // }

  // -------------------------------------------------------------------------------
  // NOTE Computed props -----------------------------------------------------------
  // -------------------------------------------------------------------------------

  // -------------------------------------------------------------------------------
  // NOTE Helpers ------------------------------------------------------------------
  // -------------------------------------------------------------------------------

  // -------------------------------------------------------------------------------
  // NOTE Requests -----------------------------------------------------------------
  // -------------------------------------------------------------------------------

  // -------------------------------------------------------------------------------
  // NOTE Subscriptions ------------------------------------------------------------
  // -------------------------------------------------------------------------------

  logout() {
    localStorage.clear();
    this.authStorage.clearUser();
    localStorage.removeItem('selectedHotelId');
    localStorage.removeItem('selectedHotelName');
    localStorage.removeItem('new_property');
    localStorage.removeItem("isAddingNewProperty");
    this.commonService.clearSelectedHotel();
    this.loggedInUser = null; // Update the property
    this.cdr.detectChanges(); // Trigger change detection
    this.router.navigate(['/account/login']);
  }

  goToPropertyListing(): void {
    this.router.navigate(['/property/overview']);
  }
}
